import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Banknote, CheckCircle2, Clock, Download, Loader2, XCircle,
} from 'lucide-react';
import { adminApi } from '../services/api';
import {
  annoncerErreur, annoncerSucces, Bandeau, EtatErreur, SqueletteCarte, SqueletteTableau,
} from '../etats';
import EtatVide from './EtatVide';

/* ═══════════════════════════════════════════════════════════════════════
   MES RETRAITS

   Trois choses que cet écran doit faire comprendre, et qu'un intervenant
   découvrirait autrement par un refus :

   · **Le solde vient des relevés VALIDÉS.** Un relevé seulement transmis
     n'y figure pas : personne n'a encore attesté que ces séances ont eu
     lieu. L'écran montre donc les deux, et dit lequel compte.

   · **Le plafond porte sur un retrait**, pas sur un mois. À qui l'on doit
     davantage, on demande plusieurs fois.

   · **Demander n'est pas être payé.** L'administration autorise, puis
     quelqu'un d'autre verse — et le versement se fait hors plateforme.
   ═══════════════════════════════════════════════════════════════════════ */

const ETATS = {
  demandee: { libelle: 'En attente', couleur: 'var(--attention)', Icone: Clock },
  autorisee: { libelle: 'Autorisée', couleur: 'var(--accent-texte)', Icone: CheckCircle2 },
  payee: { libelle: 'Versée', couleur: 'var(--succes)', Icone: CheckCircle2 },
  refusee: { libelle: 'Refusée', couleur: 'var(--alerte)', Icone: XCircle },
};

const francs = (v) => `${Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA`;
const jour = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};

export default function MesRetraits() {
  const [etat, setEtat] = useState(null);
  const [montant, setMontant] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // L'erreur de la demande reste près du formulaire ; celle du chargement
  // est gardée en objet, pour que son statut choisisse l'écran.
  const [erreur, setErreur] = useState('');
  const [erreurChargement, setErreurChargement] = useState(null);

  const charger = useCallback(async () => {
    try {
      setEtat(await adminApi.mesRetraits());
      setErreurChargement(null);
    } catch (e) {
      setErreurChargement(e);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const demander = async () => {
    setEnvoi(true); setErreur('');
    try {
      const retrait = await adminApi.demanderUnRetrait(montant, message);
      annoncerSucces(`Demande ${retrait.numero} enregistrée. `
                     + "L'administration l'examinera.");
      setMontant(''); setMessage('');
      await charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  const voirLaFacture = async (retrait) => {
    try {
      const url = URL.createObjectURL(await adminApi.factureRetrait(retrait.id));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      annoncerErreur(e.message || "La facture n'a pas pu être ouverte.");
    }
  };

  if (etat === null) {
    if (erreurChargement) return <EtatErreur erreur={erreurChargement} onReessayer={charger} />;
    return (
      <div className="space-y-5">
        <SqueletteCarte />
        <SqueletteTableau colonnes={5} lignes={3} />
      </div>
    );
  }

  if (!etat.peut_demander) {
    return (
      <div className="encart encart-info">
        <p>
          <strong>Vous n’émettez pas de relevé de prestation</strong>, il n’y a
          donc rien à retirer. Votre engagement est une convention de
          bénévolat : vos frais réels sont remboursés hors plateforme. Si un
          contrat de prestation a été signé, demandez à l’administration de le
          déclarer sur votre compte.
        </p>
      </div>
    );
  }

  const enCours = etat.retraits.some(
    (r) => r.statut === 'demandee' || r.statut === 'autorisee');

  return (
    <div className="space-y-5">
      {erreurChargement && (
        <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={charger}>Réessayer</button>}>
          L’actualisation a échoué : ces données sont peut-être anciennes.
        </Bandeau>
      )}
      {erreur && <div role="alert" className="encart encart-alerte">{erreur}</div>}

      <div className="rounded-carte bg-[var(--bg-card)] shadow-carte p-6 space-y-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="surtitre text-[var(--text-muted)]">Solde disponible</span>
            <p className="text-[26px] font-semibold text-[var(--text-bold)] leading-tight">
              {francs(etat.solde)}
            </p>
            <p className="text-[12px] text-[var(--text-muted)] mt-1">
              Issu de vos relevés <strong>validés</strong>. Un relevé
              seulement transmis n’y figure pas encore.
            </p>
          </div>
          <p className="text-[12.5px] text-[var(--text-muted)]">
            Plafond par retrait : <strong>{francs(etat.plafond)}</strong>
          </p>
        </div>

        {etat.releves_disponibles?.length > 0 && (
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Relevé</th>
                  <th scope="col">Mois</th>
                  <th scope="col" className="text-right">Reste à percevoir</th>
                </tr>
              </thead>
              <tbody>
                {etat.releves_disponibles.map((r) => (
                  <tr key={r.numero}>
                    <th scope="row" className="font-normal text-[13px]">{r.numero}</th>
                    <td className="text-[13px] text-[var(--text-muted)]">{jour(r.mois)}</td>
                    <td className="text-right text-[13px]">{francs(r.reste)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {enCours ? (
          <div className="encart encart-info">
            <p>
              Une demande est en cours. Vous pourrez en faire une autre une
              fois celle-ci traitée.
            </p>
          </div>
        ) : Number(etat.solde) <= 0 ? (
          <p className="text-[13px] text-[var(--text-muted)]">
            Rien à retirer pour l’instant : émettez un relevé, faites-le
            valider, et le montant apparaîtra ici.
          </p>
        ) : (
          <div className="pt-3 border-t border-[var(--bordure)] space-y-3">
            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-1.5"
                     htmlFor="montant-retrait">
                Montant à retirer
              </label>
              <input id="montant-retrait" type="number" min="1"
                     max={etat.plafond} value={montant}
                     onChange={(e) => setMontant(e.target.value)}
                     placeholder={`jusqu’à ${francs(Math.min(Number(etat.solde), Number(etat.plafond)))}`}
                     className="champ w-56" />
            </div>
            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-1.5"
                     htmlFor="message-retrait">
                Message (facultatif)
              </label>
              <textarea id="message-retrait" rows={2} value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Coordonnées de versement, précision…"
                        className="champ resize-none" />
            </div>
            <button type="button" onClick={demander} disabled={!montant || envoi}
                    aria-busy={envoi || undefined} className="act">
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Banknote className="w-4 h-4" />}
              Demander le retrait
            </button>
          </div>
        )}
      </div>

      {etat.retraits.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide icone={Banknote} titre="Aucune demande de retrait"
                    aide="Vos demandes et leur suivi apparaîtront ici." />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Demandé le</th>
                  <th scope="col" className="text-right">Montant</th>
                  <th scope="col">État</th>
                  <th scope="col">Relevés soldés</th>
                  <th scope="col" className="text-right">Facture</th>
                </tr>
              </thead>
              <tbody>
                {etat.retraits.map((r) => {
                  const { libelle, couleur } = ETATS[r.statut] || ETATS.demandee;
                  return (
                    <tr key={r.id}>
                      <th scope="row" className="font-normal text-[13.5px] font-medium">
                        {r.numero}
                        {r.motif_refus && (
                          <span className="block text-[11.5px]"
                                style={{ color: 'var(--alerte)' }}>
                            {r.motif_refus}
                          </span>
                        )}
                      </th>
                      <td className="text-[13px] text-[var(--text-muted)]">
                        {jour(r.demande_le)}
                      </td>
                      <td className="text-right text-[13.5px] font-semibold">
                        {francs(r.montant)}
                      </td>
                      <td>
                        <span className="text-[12px] font-semibold" style={{ color: couleur }}>
                          {libelle}
                        </span>
                        {r.statut === 'payee' && (
                          <span className="block text-[11px] text-[var(--text-muted)]">
                            le {jour(r.paye_le)}
                          </span>
                        )}
                      </td>
                      <td className="text-[12px] text-[var(--text-muted)]">
                        {r.releves.map((l) => l.numero).join(', ') || '—'}
                      </td>
                      <td className="text-right">
                        {['autorisee', 'payee'].includes(r.statut) && (
                          <button type="button" onClick={() => voirLaFacture(r)}
                                  className="act act-tertiaire" title="Facture">
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />
        Demander n’est pas être payé : l’administration autorise, puis un
        autre responsable verse. Le versement se fait <strong>hors
        plateforme</strong> — virement, espèces ou mobile money — et la
        plateforme l’enregistre. La facture paraît à l’autorisation.
      </p>
    </div>
  );
}
